import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Text,
  Accordion,
  AccordionPanel,
  Button,
  Spinner,
  Layer,
} from "grommet";
import { NoResults } from "@shared";
import { EventService } from "@services";
import { presentToastErrorContent } from "@shared/Toast";
import moment from "moment";
import { useTranslation } from "react-i18next";

// Soft pastel palette mirroring the updatedBy badge so the row has a
// single, consistent "chip" visual language.
const eventTypeStyles = {
  email: {
    background: "#EEF2FF",
    color: "#3730A3",
    border: "#C7D2FE",
    label: "Email",
  },
  sms: {
    background: "#ECFDF5",
    color: "#047857",
    border: "#A7F3D0",
    label: "SMS",
  },
  push: {
    background: "#FEF3C7",
    color: "#92400E",
    border: "#FDE68A",
    label: "Push",
  },
  keys: {
    background: "#FEF3C7",
    color: "#92400E",
    border: "#FDE68A",
    label: "Keys",
  },
  status: {
    background: "#F5F3FF",
    color: "#5B21B6",
    border: "#DDD6FE",
    label: "Status",
  },
  update: {
    background: "#E0F2FE",
    color: "#075985",
    border: "#BAE6FD",
    label: "Update",
  },
  pms: {
    background: "#F1F5F9",
    color: "#334155",
    border: "#E2E8F0",
    label: "PMS",
  },
  charge: {
    background: "#FFF7ED",
    color: "#9A3412",
    border: "#FED7AA",
    label: "Charge",
  },
  pms_key_cutter: {
    background: "#F1F5F9",
    color: "#334155",
    border: "#E2E8F0",
    label: "Key cutter",
  },
};

const DEFAULT_TYPE_STYLE = {
  background: "#F3F4F6",
  color: "#4B5563",
  border: "#E5E7EB",
  label: "Event",
};

// Per-status tint applied to the event card background + border so the
// row's delivery state (set by the Twilio / SES webhook) is visible at a
// glance. Only used for type=sms / type=email events that carry a status.
const eventStatusStyles = {
  SENT: {
    background: "#f8fcff",
    border: "#E5E7EB",
    label: "Sent",
    color: "#4B5563",
  },
  DELIVERED: {
    background: "#F0FDF4",
    border: "#BBF7D0",
    label: "Delivered",
    color: "#15803D",
  },
  FAILED: {
    background: "#FEF2F2",
    border: "#FECACA",
    label: "Failed",
    color: "#B91C1C",
  },
};

const DEFAULT_CARD_STYLE = {
  background: "#f8fcff",
  border: "#E5E7EB",
};

export default function EventList({ bookingId, timezone }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [isLoadingOlderEvents, setIsLoadingOlderEvents] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [longMessage, setLongMessage] = useState(null);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const openEventDetails = useCallback(async (event) => {
    if (event.type === "email" && event.template) {
      setEmailPreviewLoading(true);
      try {
        let context = {};
        if (event.longMessage) {
          try {
            context = JSON.parse(event.longMessage);
          } catch {
            context = {};
          }
        }
        const res = await EventService.renderEmail(
          event.template,
          event.lang,
          context,
        );
        setEmailPreviewHtml(res.data.html);
      } catch (error) {
        presentToastErrorContent(error);
        setLongMessage(event.longMessage);
      } finally {
        setEmailPreviewLoading(false);
      }
      return;
    }
    setLongMessage(event.longMessage);
  }, []);

  const loadEvents = useCallback(() => {
    EventService.getEvents(bookingId).then(
      async (response) => {
        if (response.data) {
          const { events: nextEvents, hasMoreEvents: nextHasMoreMessage } =
            response.data;
          setEvents(nextEvents);
          setHasMoreEvents(nextHasMoreMessage);
        }
        setIsLoadingInitial(false);
      },
      (error) => {
        presentToastErrorContent(error);
        setIsLoadingInitial(false);
      },
    );
  }, [bookingId]);

  const loadOlderEvents = async () => {
    setIsLoadingOlderEvents(true);
    const { data } = await EventService.getEvents(
      bookingId,
      events[events.length - 1]._id,
    );
    const { events: olderEvents, hasMoreEvents: nextHasMoreMessage } = data;
    setEvents((prevEvents) => {
      return [...prevEvents, ...olderEvents];
    });
    setHasMoreEvents(nextHasMoreMessage);
    setIsLoadingOlderEvents(false);
  };

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  if (isLoadingInitial) {
    return (
      <Box align="center" pad="large">
        <Spinner size="medium" />
      </Box>
    );
  }

  return events.length === 0 ? (
    <NoResults>{t("common.emptyList")}</NoResults>
  ) : (
    <>
      {longMessage && (
        <Layer
          position="center"
          onClickOutside={() => setLongMessage(null)}
          onEsc={() => setLongMessage(null)}
          background="light-3"
        >
          <Box
            pad="small"
            round
            width={{ max: "large" }}
            height={{ max: "large" }}
            overflow={{ vertical: "auto" }}
          >
            {longMessage.split(/\n/).map((chunk, i) => (
              <Text key={`chunk-long-${i}`}>{chunk}</Text>
            ))}
            <Button
              label={t("common.close")}
              onClick={() => setLongMessage(null)}
              small
              alignSelf="center"
            />
          </Box>
        </Layer>
      )}
      {emailPreviewHtml && (
        <Layer
          onEsc={() => setEmailPreviewHtml(null)}
          onClickOutside={() => setEmailPreviewHtml(null)}
          responsive
          margin="medium"
        >
          <Box width={{ max: "750px", min: "650px" }} height={{ max: "90vh" }}>
            <Box
              direction="row"
              justify="between"
              align="center"
              pad={{ horizontal: "medium", vertical: "small" }}
              border={{ side: "bottom", color: "light-4" }}
              flex={false}
            >
              <Text weight="bold">{t("localizedEmail.emailPreview")}</Text>
              <Button
                label={t("common.close")}
                size="small"
                onClick={() => setEmailPreviewHtml(null)}
              />
            </Box>
            <Box flex overflow="auto" pad="none">
              <iframe
                srcDoc={emailPreviewHtml}
                title={t("localizedEmail.emailPreview")}
                sandbox=""
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: 500,
                  border: "none",
                }}
              />
            </Box>
          </Box>
        </Layer>
      )}
      <Box width="xlarge">
        {events.map((event, idx) => {
          const typeStyle = eventTypeStyles[event.type] || DEFAULT_TYPE_STYLE;
          const statusStyle = event.status
            ? eventStatusStyles[event.status]
            : null;
          const cardStyle = statusStyle || DEFAULT_CARD_STYLE;
          const isFirst = idx === 0;
          const isLast = idx === events.length - 1;
          const NODE_SIZE = 16;
          // Vertical offset from the top of the row to the node center,
          // so the rail is clipped exactly at the first/last node center.
          const NODE_CENTER_OFFSET = 22;
          const eventMoment = moment.tz(event.createdAt, timezone);
          const isExpanded = expandedIds.has(event._id);
          const minuteKey = eventMoment.format("YYYY-MM-DD HH:mm");
          const prevEvent = idx > 0 ? events[idx - 1] : null;
          const prevMinuteKey = prevEvent
            ? moment
                .tz(prevEvent.createdAt, timezone)
                .format("YYYY-MM-DD HH:mm")
            : null;
          const isGroupedWithPrev = prevMinuteKey === minuteKey;
          return (
            <Box key={event._id} direction="row" align="start">
              {/* Col 1 — Timestamp */}
              <Box
                flex={false}
                width="110px"
                align="end"
                pad={{ right: "small" }}
                style={{ paddingTop: NODE_CENTER_OFFSET - 16 }}
              >
                {!isGroupedWithPrev && (
                  <>
                    <Text size="xsmall" color="dark-3">
                      {eventMoment.format("MMM D, YYYY")}
                    </Text>
                    <Text size="small" color="dark-1">
                      {eventMoment.format("h:mm A")}
                    </Text>
                  </>
                )}
              </Box>

              {/* Col 2 — Timeline rail + node */}
              <Box
                flex={false}
                width="32px"
                align="center"
                style={{ position: "relative", alignSelf: "stretch" }}
              >
                <Box
                  style={{
                    position: "absolute",
                    top: isFirst ? NODE_CENTER_OFFSET : 0,
                    bottom:
                      isLast && !isGroupedWithPrev
                        ? `calc(100% - ${NODE_CENTER_OFFSET}px)`
                        : 0,
                    left: "50%",
                    width: 2,
                    marginLeft: -1,
                    background: "#7d7d7d",
                  }}
                />
                {!isGroupedWithPrev && (
                  <Box
                    flex={false}
                    title={typeStyle.label}
                    background="#FFFFFF"
                    style={{
                      position: "absolute",
                      top: NODE_CENTER_OFFSET - NODE_SIZE / 2,
                      border: `3px solid ${typeStyle.color}`,
                      borderRadius: 999,
                      width: NODE_SIZE,
                      height: NODE_SIZE,
                    }}
                  />
                )}
              </Box>

              {/* Col 3 — Independent expandable card */}
              <Box
                flex
                margin={{ left: "small", bottom: "xsmall" }}
                background={cardStyle.background}
                style={{
                  border: `1px solid ${cardStyle.border}`,
                  borderRadius: 6,
                }}
              >
                <Accordion
                  onActive={(active) =>
                    setExpandedIds((prev) => {
                      const next = new Set(prev);
                      if (active.length > 0) {
                        next.add(event._id);
                      } else {
                        next.delete(event._id);
                      }
                      return next;
                    })
                  }
                >
                  <AccordionPanel
                    label={
                      <Box
                        direction="row"
                        align="center"
                        pad={{ horizontal: "small", vertical: "small" }}
                        gap="small"
                      >
                        <Box
                          flex={false}
                          align="center"
                          justify="center"
                          background={typeStyle.background}
                          style={{
                            border: `1px solid ${typeStyle.border}`,
                            borderRadius: 4,
                            padding: "1px 6px",
                            lineHeight: 1,
                            opacity: isExpanded ? 0.45 : 1,
                          }}
                        >
                          <Text
                            size="10px"
                            weight={600}
                            color={typeStyle.color}
                            style={{
                              letterSpacing: 0.4,
                              textTransform: "uppercase",
                            }}
                          >
                            {typeStyle.label}
                          </Text>
                        </Box>
                        <Text
                          truncate
                          color={
                            isExpanded
                              ? "dark-5"
                              : event.error
                                ? "red"
                                : undefined
                          }
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          {event.message}
                        </Text>
                      </Box>
                    }
                  >
                    <Box pad="small" gap="xsmall">
                      {statusStyle && (
                        <Box direction="row" align="center" gap="xsmall">
                          <Box
                            flex={false}
                            background={statusStyle.background}
                            style={{
                              border: `1px solid ${statusStyle.border}`,
                              borderRadius: 4,
                              padding: "1px 6px",
                              lineHeight: 1,
                            }}
                          >
                            <Text
                              size="10px"
                              weight={600}
                              color={statusStyle.color}
                              style={{
                                letterSpacing: 0.4,
                                textTransform: "uppercase",
                              }}
                            >
                              {statusStyle.label}
                            </Text>
                          </Box>
                          {event.deliveredAt &&
                            event.status === "DELIVERED" && (
                              <Text size="xsmall" color="dark-4">
                                {moment
                                  .tz(event.deliveredAt, timezone)
                                  .format("MMM D, h:mm A")}
                              </Text>
                            )}
                        </Box>
                      )}
                      {event.error && <Text color="red">{event.error}</Text>}
                      {event.message.split(/\n/).map((chunk, i) => (
                        <Text key={`chunk-${event.id}-${i}`}>{chunk}</Text>
                      ))}
                      {event.longMessage && (
                        <Button
                          label={
                            event.type === "pms"
                              ? t("eventList.viewPayload")
                              : event.type === "email" && event.template
                                ? emailPreviewLoading
                                  ? t("common.loading")
                                  : t("eventList.viewEmail")
                                : t("eventList.viewMore")
                          }
                          onClick={() => openEventDetails(event)}
                          disabled={emailPreviewLoading}
                          primary
                          small
                          alignSelf="start"
                          margin={{ top: "small" }}
                        />
                      )}
                      {event.updatedBy && (
                        <Text
                          size="xsmall"
                          color="dark-4"
                          alignSelf="end"
                          style={{ fontStyle: "italic" }}
                        >
                          {event.updatedBy}
                        </Text>
                      )}
                    </Box>
                  </AccordionPanel>
                </Accordion>
              </Box>
            </Box>
          );
        })}
      </Box>
      {hasMoreEvents && (
        <Box center>
          <Button
            style={
              isLoadingOlderEvents
                ? {
                    paddingLeft: 90,
                    width: 205,
                  }
                : {}
            }
            label={
              isLoadingOlderEvents ? (
                <Spinner size="xsmall" />
              ) : (
                t("eventList.loadOlder")
              )
            }
            onClick={loadOlderEvents}
            active
            alignSelf="center"
            small
          />
        </Box>
      )}
    </>
  );
}
